'use strict';

// Pure checkout-basket state: line items, quantity rules, and display totals.
// Prices here are for display only; shop-create-order re-prices every line
// from its own catalog, so only ids and quantities are ever sent to it.

const catalog = require('./product-catalog');

const initialState = { items: [] };

function firstImageSrc(product) {
  const media = product?.images?.[0];
  if (!media) return null;
  if (typeof media === 'string') return media;
  return media.thumbnail || media.src || null;
}

function clampQuantity(quantity, max = catalog.MAX_QUANTITY_PER_ITEM) {
  const n = Math.floor(Number(quantity));
  if (!Number.isFinite(n) || n < 1) return 1;
  return Math.min(n, Math.max(1, max));
}

function itemKey(item) {
  return item.key || item.productId;
}

function maxQuantityForItem(item) {
  return catalog.maxQuantityFor(item?.productId);
}

// Builds a line item, pricing in the selected variant and casing option.
// Returns null when the product (or selection) is not purchasable.
function buildCartItem(product, quantity, variant, casing) {
  const hasVariantPrice = variant && Number.isFinite(Number(variant.price));
  const staticPrice = hasVariantPrice
    ? Number(variant.price) + Number(casing?.price || 0)
    : Number(product?.price);
  const unitPrice = catalog.unitPriceFor(product?.id, variant?.id, casing?.id, staticPrice);
  const maxQuantity = catalog.maxQuantityFor(product?.id);

  if (
    product?.purchasable === false ||
    !Number.isFinite(unitPrice) ||
    unitPrice <= 0 ||
    maxQuantity < 1
  ) {
    return null;
  }

  const nameParts = [product.name];
  if (variant?.name) nameParts.push(variant.name);
  if (casing?.name && Number(casing?.price || 0) > 0) nameParts.push(casing.name);

  return {
    key: [product.id, variant?.id, casing?.id].filter(Boolean).join('::'),
    productId: product.id,
    variantId: variant?.id || null,
    casingId: casing?.id || null,
    name: nameParts.join(' — '),
    price: unitPrice,
    priceDisplay: `₹${unitPrice.toLocaleString('en-IN')}`,
    image: firstImageSrc(product),
    quantity: clampQuantity(quantity, maxQuantity),
  };
}

function cartReducer(state, action) {
  switch (action.type) {
    case 'BUY_NOW': {
      // Buy now starts a fresh checkout containing only this product.
      const item = buildCartItem(action.product, action.quantity, action.variant, action.casing);
      if (!item) return state;
      return { ...state, items: [item] };
    }
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter((i) => itemKey(i) !== action.key) };
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map((i) =>
          itemKey(i) === action.key
            ? { ...i, quantity: clampQuantity(action.quantity, maxQuantityForItem(i)) }
            : i
        ),
      };
    case 'CLEAR_CART':
      return initialState;
    case 'REPRICE':
      // Sync stored unit prices with live pricing so checkout always shows
      // what the order will actually charge.
      return {
        ...state,
        items: state.items.map((i) => {
          const price = catalog.unitPriceFor(i.productId, i.variantId, i.casingId, i.price);
          if (price === i.price) return i;
          return { ...i, price, priceDisplay: `₹${price.toLocaleString('en-IN')}` };
        }),
      };
    case 'HYDRATE':
      // Normalize items persisted by older versions (no key, image objects, out-of-range qty).
      return {
        ...action.state,
        items: (action.state.items || []).map((i) => ({
          ...i,
          key: itemKey(i),
          quantity: clampQuantity(i.quantity, maxQuantityForItem(i)),
          image:
            i.image && typeof i.image === 'object'
              ? i.image.thumbnail || i.image.src || null
              : i.image,
        })),
      };
    default:
      return state;
  }
}

/**
 * Unit and line price in the shopper's currency (INR, or USD for the
 * international region) after any flash and bulk discount. `productQuantity`
 * is the product's total units across the order (defaults to this line's).
 */
function linePrice(
  unitInr,
  quantity,
  { productId, flashActive = false, isInternational = false, productQuantity = quantity } = {}
) {
  const bulkDiscount = catalog.bulkDiscountApplies(productQuantity);
  const sellingUnit = catalog.sellingInr(unitInr, flashActive, productId);
  if (isInternational) {
    const unitPrice = catalog.bulkDiscountedUsd(catalog.inrToUsd(sellingUnit), productQuantity);
    return { unitPrice, lineTotal: catalog.roundCents(unitPrice * quantity), bulkDiscount };
  }
  const unitPrice = catalog.bulkDiscountedInr(sellingUnit, productQuantity);
  return { unitPrice, lineTotal: unitPrice * quantity, bulkDiscount };
}

/**
 * Display totals for the checkout summary. INR uses Cashfree shipping; the
 * international region uses PayPal USD pricing and shipping.
 */
function checkoutTotals(items, { flashActive = false, isInternational = false } = {}) {
  const lineItems = items || [];
  const productQty = catalog.quantitiesByProduct(lineItems);
  const lines = lineItems.map((item) => ({
    key: itemKey(item),
    ...linePrice(item.price, item.quantity, {
      productId: item.productId,
      flashActive,
      isInternational,
      productQuantity: productQty[item.productId],
    }),
  }));
  const itemCount = lineItems.reduce((sum, item) => sum + item.quantity, 0);

  if (isInternational) {
    const paypal = catalog.paypalTotalsForItems(lineItems, flashActive);
    return { lines, itemCount, ...paypal };
  }
  const subtotal = lines.reduce((sum, line) => sum + line.lineTotal, 0);
  const shipping = lineItems.length ? catalog.shippingFeeForItems(lineItems) : 0;
  return { lines, itemCount, subtotal, shipping, total: subtotal + shipping };
}

/** Line items for shop-create-order: ids and quantities only, never prices. */
function orderLineItems(items) {
  return (items || []).map((item) => ({
    productId: item.productId,
    variantId: item.variantId || null,
    casingId: item.casingId || null,
    quantity: clampQuantity(item.quantity, maxQuantityForItem(item)),
  }));
}

module.exports = {
  initialState,
  buildCartItem,
  cartReducer,
  checkoutTotals,
  clampQuantity,
  itemKey,
  linePrice,
  maxQuantityForItem,
  orderLineItems,
};
